import { useEffect, useId, useState } from 'react';
import { NavLink, useLocation, useResolvedPath } from 'react-router-dom';
import { t } from '../i18n/i18n';
import './NavItem.scss';

const iconVar = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * Sidebar nav item — `.ui_nav-item-2` in the Style Guide.
 * 40px · r10 · idle #777 · active wash #004BFF33.
 *
 * Pass `items` and it becomes a group (`.ui_nav-group` in the live app): the
 * row reveals its children and is itself a link to `to`, the group's index
 * route — so opening Payout & Certificates lands on Payout rather than leaving
 * the sub-nav hanging open with nothing selected.
 *
 * That last part is deliberately unlike the live app, where the parent row is
 * an inert `div` and only the children navigate.
 */
export default function NavItem({ to, icon, label, items, end, section, badge, onNavigate }) {
  const { pathname } = useLocation();
  const { pathname: base } = useResolvedPath(to);
  const subId = useId();
  const hasChildren = Boolean(items?.length);
  const withinGroup = pathname === base || pathname.startsWith(`${base}/`);
  // A section row (Affiliate, Payment & Verification) owns a whole subtree
  // whose nav lives in its own aside, so it stays lit on every page under it.
  const isMatch = section ? withinGroup : false;

  const [prevWithinGroup, setPrevWithinGroup] = useState(withinGroup);
  const [open, setOpen] = useState(withinGroup);

  // Synchronously update open state during render when navigating away or in
  if (withinGroup !== prevWithinGroup) {
    setPrevWithinGroup(withinGroup);
    setOpen(withinGroup);
  }

  const isOpen = withinGroup ? open : false;

  if (!hasChildren) {
    return (
      <NavLink
        to={to}
        end={section ? false : end}
        onClick={onNavigate}
        // Read by the sidebar to label the rail's icon-only rows on hover —
        // see the `rail-tip` handling in Sidebar.jsx.
        data-label={t(label)}
        className={({ isActive }) => `nav-item${isActive || isMatch ? ' is-active' : ''}`}
      >
        <span className="icon nav-item__icon" style={iconVar(icon)} />
        <span className="nav-item__text">{t(label)}</span>
        {badge && <span className="nav-item__badge">{t(badge)}</span>}
      </NavLink>
    );
  }

  return (
    <div className={`nav-group${isOpen ? ' is-open' : ''}`}>
      {/* It only toggles when the click changes nothing else — when you are
          already on the index route. Anywhere else the click is a navigation
          first, so it opens: collapsing would hide the child that just became
          current, including when coming from a sibling like Certificates. */}
      <NavLink
        to={to}
        className={`nav-item nav-item--toggle${withinGroup ? ' is-active' : ''}`}
        data-label={t(label)}
        aria-expanded={isOpen}
        aria-controls={subId}
        onClick={() => {
          setOpen((wasOpen) => (pathname === base ? !wasOpen : true));
          onNavigate?.();
        }}
      >
        <span className="icon nav-item__icon" style={iconVar(icon)} />
        <span className="nav-item__text">{t(label)}</span>
        <span className="icon nav-item__chevron" style={iconVar('chevron-down')} />
      </NavLink>

      {/* When leaving the group (!withinGroup), display: none collapses the submenu
          instantly with zero delay, so following items (like Competition) are immediately
          at their final resting position for the travelling nav slider (.nav-fill). */}
      <div
        className="nav-group__sub"
        id={subId}
        inert={!isOpen}
        style={!withinGroup ? { display: 'none' } : undefined}
      >
        <div className="nav-group__inner">
          {items.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              end={child.end ?? true}
              onClick={onNavigate}
              tabIndex={isOpen ? undefined : -1}
              className={({ isActive }) => `nav-subitem${isActive ? ' is-active' : ''}`}
            >
              {t(child.label)}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}
